import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';
import { subscribeToTestimonials } from '../services/firebase';
import TiltCard from './ui/TiltCard';

const AUTO_ADVANCE_INTERVAL = 5000; // 5 seconds

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const autoAdvanceRef = useRef(null);

  // Subscribe to testimonials data from Firebase
  useEffect(() => {
    const unsubscribe = subscribeToTestimonials((data) => {
      setTestimonials(data);
      setLoading(false);
      // Reset current index if data changes
      if (data.length > 0 && currentIndex >= data.length) {
        setCurrentIndex(0);
      }
    });

    return () => unsubscribe();
  }, []);

  const nextTestimonial = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prevTestimonial = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  const goToTestimonial = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  // Auto-play: advance every 5 seconds, pause on hover
  useEffect(() => {
    if (testimonials.length <= 1 || isHovered) return;

    autoAdvanceRef.current = setInterval(() => {
      nextTestimonial();
    }, AUTO_ADVANCE_INTERVAL);

    return () => {
      if (autoAdvanceRef.current) {
        clearInterval(autoAdvanceRef.current);
        autoAdvanceRef.current = null;
      }
    };
  }, [testimonials.length, isHovered, nextTestimonial]);

  return (
    <section id="testimonials" className="py-16 sm:py-20 md:py-24 lg:py-32 bg-gradient-to-b from-[#0A0A0A] to-black relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-1/4 left-10 w-96 h-96 bg-[#D4AF7A]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-[#C9A882]/5 rounded-full blur-3xl"></div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#D4AF7A]/10 border border-[#D4AF7A]/30 rounded-full text-[#D4AF7A] text-sm font-semibold mb-4">
            Testimoni
          </span>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[#F7F5F2] mb-6">
            Kata Mereka
          </h2>
          {/* Decorative gold divider */}
          <div className="divider-gold-thick w-32 mx-auto mb-3"></div>
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="text-[#D4AF7A]/40 text-lg">&#x2726;</span>
            <span className="text-[#D4AF7A]/20 text-base">&#x2727;</span>
            <span className="text-[#D4AF7A]/40 text-lg">&#x2726;</span>
          </div>
          <p className="text-sm sm:text-base text-[#F7F5F2]/70 max-w-2xl mx-auto lg:text-lg">
            Kepuasan klien adalah kebanggaan kami
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF7A]"></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && testimonials.length === 0 && (
          <div className="text-center py-20">
            <p className="text-[#F7F5F2]/70 text-lg">Belum ada testimoni yang tersedia.</p>
          </div>
        )}

        {/* Testimonials Content */}
        {!loading && testimonials.length > 0 && (
          <>
        {/* Testimonial Carousel */}
        <div
          className="relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Main Testimonial Card */}
          <TiltCard tiltDegree={4} scale={1.01}>
          <div className="glass-effect-card card-luxury rounded-2xl p-8 sm:p-12 relative overflow-hidden">
            {/* Gold gradient accent bar on the left */}
            <div className="absolute left-0 top-4 bottom-4 w-[2px] bg-gradient-to-b from-[#C9A882] via-[#D4AF7A] to-transparent rounded-full"></div>

            {/* Quote Icon */}
            <div className="absolute top-8 right-8 text-[#D4AF7A]/15">
              <Quote size={96} fill="currentColor" />
            </div>

            {/* Content */}
            <div className="relative z-10">
              {/* Stars */}
              <div className="flex gap-1 mb-6 animate-gold-pulse">
                {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                  <Star key={i} size={24} className="text-[#D4AF7A] drop-shadow-[0_0_6px_rgba(212,175,122,0.5)]" fill="#D4AF7A" />
                ))}
              </div>

              {/* Testimonial Text */}
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-[#F7F5F2] leading-relaxed mb-8 font-light">
                "{testimonials[currentIndex].text}"
              </p>

              {/* Author Info */}
              <div className="flex items-center gap-4">
                <div className="relative shrink-0">
                  <img 
                    src={testimonials[currentIndex].image}
                    alt={testimonials[currentIndex].name}
                    className="w-16 h-16 rounded-full object-cover ring-2 ring-[#D4AF7A]/50 ring-offset-2 ring-offset-black"
                    onError={(e) => {
                      e.target.src = 'https://ui-avatars.com/api/?name=' + testimonials[currentIndex].name + '&background=D4AF7A&color=000';
                    }}
                  />
                </div>
                <div>
                  <h4 className="text-base sm:text-lg font-semibold text-[#F7F5F2]">
                    {testimonials[currentIndex].name}
                  </h4>
                  <p className="text-[#D4AF7A]">{testimonials[currentIndex].role}</p>
                </div>
              </div>
            </div>
          </div>
          </TiltCard>

          {/* Navigation Buttons */}
          <div className="flex justify-center items-center gap-4 mt-8">
            <button
              onClick={prevTestimonial}
              className="p-3 bg-white/5 hover:bg-[#D4AF7A]/20 border border-[#D4AF7A]/30 rounded-full text-[#F7F5F2] transition-all duration-300 hover:scale-110 hover:shadow-[0_0_20px_rgba(212,175,122,0.3)] hover:border-[#D4AF7A]/60"
            >
              <ChevronLeft size={24} />
            </button>

            {/* Dots Indicator */}
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonial(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentIndex
                      ? 'w-8 h-3 bg-gradient-to-r from-[#C9A882] to-[#D4AF7A] shadow-[0_0_8px_rgba(212,175,122,0.5)]'
                      : 'w-3 h-3 bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="p-3 bg-white/5 hover:bg-[#D4AF7A]/20 border border-[#D4AF7A]/30 rounded-full text-[#F7F5F2] transition-all duration-300 hover:scale-110 hover:shadow-[0_0_20px_rgba(212,175,122,0.3)] hover:border-[#D4AF7A]/60"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        {/* All Testimonials Grid (Small) */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {testimonials.map((testimonial, index) => (
            <TiltCard key={testimonial.id} tiltDegree={3} scale={1.01}>
            <div 
              onClick={() => goToTestimonial(index)}
              className={`glass-effect-card rounded-xl cursor-pointer transition-all duration-300 card-luxury ${
                index === currentIndex
                  ? 'border-[#D4AF7A]/60 shadow-[0_0_16px_rgba(212,175,122,0.15)]'
                  : 'border-[#D4AF7A]/10 hover:border-[#D4AF7A]/30'
              }`}
            >
              <div className="p-4">
                <div className="flex items-center gap-3 mb-2">
                  <img 
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-10 h-10 rounded-full object-cover ring-1 ring-[#D4AF7A]/40 ring-offset-1 ring-offset-black"
                    onError={(e) => {
                      e.target.src = 'https://ui-avatars.com/api/?name=' + testimonial.name + '&background=D4AF7A&color=000';
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <h5 className="text-sm font-semibold text-[#F7F5F2] truncate">{testimonial.name}</h5>
                    <p className="text-xs text-[#D4AF7A]">{testimonial.role}</p>
                  </div>
                </div>
                <div className="flex gap-0.5">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={12} className="text-[#D4AF7A]" fill="#D4AF7A" />
                  ))}
                </div>
              </div>
            </div>
            </TiltCard>
          ))}
        </div>
        </>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
